const MONTHS_IN_YEAR = 12;
const monthLabelFormatter = new Intl.DateTimeFormat("fr-FR", { month: "long" });

const padMonth = (value) => String(value).padStart(2, "0");

const toDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = value instanceof Date ? new Date(value) : new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const toMonthKey = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value.trim())) {
    return value.trim();
  }

  const parsedDate = toDate(value);
  if (!parsedDate) {
    return "";
  }

  return `${parsedDate.getFullYear()}-${padMonth(parsedDate.getMonth() + 1)}`;
};

const toFiniteNumber = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const normalizedValue = String(value).trim().replace(/\s+/g, "").replace(/,/g, ".");
  if (!normalizedValue) {
    return null;
  }

  const numericValue = Number.parseFloat(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const toTitleCase = (value) => {
  if (!value) {
    return "";
  }

  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
};

const asEntryArray = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload instanceof Map) {
    return Array.from(payload.entries()).map(([month, value]) => ({
      month,
      declared_days: value,
    }));
  }

  if (payload && typeof payload === "object") {
    return Object.entries(payload).map(([month, value]) => {
      if (value && typeof value === "object") {
        return {
          month,
          ...value,
        };
      }

      return {
        month,
        declared_days: value,
      };
    });
  }

  return [];
};

const resolveFallbackDaysForMonth = (fallbackDays, monthKey) => {
  if (fallbackDays instanceof Map) {
    return toFiniteNumber(fallbackDays.get(monthKey));
  }

  if (Array.isArray(fallbackDays)) {
    const monthEntry = fallbackDays.find((entry) => {
      const entryMonthKey = toMonthKey(entry?.month || entry?.key);
      return entryMonthKey === monthKey;
    });

    return toFiniteNumber(
      monthEntry?.forecast_days ??
        monthEntry?.days_forecast ??
        monthEntry?.days_per_month ??
        monthEntry?.jours_mois ??
        monthEntry?.value,
    );
  }

  if (fallbackDays && typeof fallbackDays === "object") {
    if (Object.prototype.hasOwnProperty.call(fallbackDays, monthKey)) {
      const monthValue = fallbackDays[monthKey];

      if (monthValue && typeof monthValue === "object") {
        return toFiniteNumber(
          monthValue?.forecast_days ??
            monthValue?.days_forecast ??
            monthValue?.days_per_month ??
            monthValue?.jours_mois ??
            monthValue?.value,
        );
      }

      return toFiniteNumber(monthValue);
    }

    return toFiniteNumber(
      fallbackDays?.forecast_days ??
        fallbackDays?.days_forecast ??
        fallbackDays?.days_per_month ??
        fallbackDays?.jours_mois ??
        fallbackDays?.value,
    );
  }

  return toFiniteNumber(fallbackDays);
};

export const getTimesheetYear = (now = new Date()) => {
  const referenceDate = toDate(now) || new Date();
  return referenceDate.getFullYear();
};

export const getContractStartMonthIndex = (contractStartDate, year) => {
  if (!Number.isInteger(year)) {
    return null;
  }

  const parsedStartDate = toDate(contractStartDate);
  if (!parsedStartDate) {
    return null;
  }

  const contractStartYear = parsedStartDate.getFullYear();

  if (contractStartYear > year) {
    return null;
  }

  if (contractStartYear < year) {
    return 0;
  }

  return parsedStartDate.getMonth();
};

export const getAvailableMonthsForTimesheet = ({
  contractStartDate,
  contractEndDate,
  year,
  now = new Date(),
} = {}) => {
  const targetYear = Number.isInteger(year) ? year : getTimesheetYear(now);
  const startMonthIndex = getContractStartMonthIndex(contractStartDate, targetYear);

  if (startMonthIndex === null) {
    return [];
  }

  let endMonthIndex = MONTHS_IN_YEAR - 1;

  if (contractEndDate) {
    const parsedEndDate = toDate(contractEndDate);
    if (parsedEndDate) {
      const contractEndYear = parsedEndDate.getFullYear();
      if (contractEndYear < targetYear) {
        return [];
      } else if (contractEndYear === targetYear) {
        endMonthIndex = parsedEndDate.getMonth();
      }
    }
  }

  if (endMonthIndex < startMonthIndex) {
    return [];
  }

  return Array.from({ length: endMonthIndex - startMonthIndex + 1 }, (_, offset) => {
    const monthIndex = startMonthIndex + offset;
    const monthDate = new Date(targetYear, monthIndex, 1);

    return {
      key: `${targetYear}-${padMonth(monthIndex + 1)}`,
      label: toTitleCase(monthLabelFormatter.format(monthDate)),
      monthIndex,
      year: targetYear,
    };
  });
};

export const normalizeMonthlyDeclaredDays = (timesheetsPayload) => {
  const normalizedDeclaredDays = new Map();

  asEntryArray(timesheetsPayload).forEach((entry) => {
    const monthKey = toMonthKey(entry?.month || entry?.key);
    if (!monthKey) {
      return;
    }

    const declaredDays = toFiniteNumber(
      entry?.declared_days ?? entry?.days_declared ?? entry?.value,
    );

    if (declaredDays === null) {
      return;
    }

    normalizedDeclaredDays.set(monthKey, Math.max(0, declaredDays));
  });

  return normalizedDeclaredDays;
};

export const getMonthlyDays = ({
  monthKey,
  declaredDaysMap,
  fallbackDays,
} = {}) => {
  const normalizedMonthKey = toMonthKey(monthKey);
  const normalizedDeclaredDays =
    declaredDaysMap instanceof Map
      ? declaredDaysMap
      : normalizeMonthlyDeclaredDays(declaredDaysMap);

  if (normalizedMonthKey && normalizedDeclaredDays.has(normalizedMonthKey)) {
    return {
      source: "declared",
      days: Math.max(0, toFiniteNumber(normalizedDeclaredDays.get(normalizedMonthKey)) ?? 0),
    };
  }

  const resolvedFallbackDays = resolveFallbackDaysForMonth(fallbackDays, normalizedMonthKey);

  return {
    source: "forecast",
    days: Math.max(0, resolvedFallbackDays ?? 0),
  };
};

export const getTimesheetMonthContext = (monthKey, now = new Date()) => {
  const normalizedMonthKey = toMonthKey(monthKey);
  const currentMonthKey = toMonthKey(now);

  return {
    monthKey: normalizedMonthKey,
    currentMonthKey,
    isFuture: Boolean(normalizedMonthKey) && normalizedMonthKey > currentMonthKey,
    isPast: Boolean(normalizedMonthKey) && normalizedMonthKey < currentMonthKey,
    isCurrent: Boolean(normalizedMonthKey) && normalizedMonthKey === currentMonthKey,
  };
};

export const getTimesheetMonthStatusLabel = ({
  monthKey,
  locked = false,
  now = new Date(),
} = {}) => {
  if (locked) {
    return "Clôturé";
  }

  return getTimesheetMonthContext(monthKey, now).isFuture ? "Prévision" : "Ouvert";
};

const isMonthWithinWindow = (record, monthKey, startFields, endFields) => {
  const normalizedMonthKey = toMonthKey(monthKey);
  if (!record || !normalizedMonthKey) {
    return false;
  }

  const [yearStr, monthStr] = normalizedMonthKey.split("-");
  const yearNum = Number.parseInt(yearStr, 10);
  const monthNum = Number.parseInt(monthStr, 10);

  if (!Number.isInteger(yearNum) || !Number.isInteger(monthNum)) {
    return false;
  }

  const parseDateToUTC = (rawDate) => {
    if (!rawDate) return null;
    let d = rawDate;
    if (typeof rawDate === "string") {
      const match = rawDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return Date.UTC(
          Number.parseInt(match[1], 10),
          Number.parseInt(match[2], 10) - 1,
          Number.parseInt(match[3], 10)
        );
      }
      d = new Date(rawDate);
    }
    if (d instanceof Date && !Number.isNaN(d.getTime())) {
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    }
    return null;
  };

  const monthStartUTC = Date.UTC(yearNum, monthNum - 1, 1);
  const monthEndUTC = Date.UTC(yearNum, monthNum, 0);

  const startRaw = startFields.map((field) => record?.[field]).find(Boolean);
  if (startRaw) {
    const startUTC = parseDateToUTC(startRaw);
    if (startUTC === null || startUTC > monthEndUTC) {
      return false;
    }
  }

  const endRaw = endFields.map((field) => record?.[field]).find(Boolean);
  if (endRaw) {
    const endUTC = parseDateToUTC(endRaw);
    if (endUTC === null || endUTC < monthStartUTC) {
      return false;
    }
  }

  return true;
};

export const isMonthWithinMissionWindow = (mission, monthKey) =>
  isMonthWithinWindow(mission, monthKey, ["start_date"], ["end_date"]);

export const isCollaboratorActiveForMonth = (collaborator, monthKey) =>
  isMonthWithinWindow(
    collaborator,
    monthKey,
    ["employment_start_date", "entry_date"],
    ["employment_end_date", "exit_date"],
  );
